import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import Header from "./Header";
import Title from "../components/typography/Title";
import { LAST_UPDATED_ISO, LAST_UPDATED_READABLE } from "./PrivacyPolicy";

function TermsAndConditions() {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        width: "100%",
        boxSizing: "border-box",
        color: theme.palette.text.primary,
        display: "flex",
        padding: 2,
      }}
    >
      {/* <Header /> */}
      <Stack sx={{ alignItems: "center", marginTop: "5%" }}>
        <Title size="lg" as="h1">
          Terms And Conditions
        </Title>
        <Typography variant="subtitle1">
          Last updated:{" "}
          <time dateTime={LAST_UPDATED_ISO}>{LAST_UPDATED_READABLE}</time>
        </Typography>
      </Stack>
      <Stack spacing={2} sx={{ margin: "5%", boxSizing: "border-box" }}>
        <Typography variant="h4">1. Introduction</Typography>
        <Typography>
          Welcome to Mystina AI Labs Private Limited (“Company”, “we”, “our”,
          “us”)! These Terms of Service (“Terms”, “Terms of Service”) govern
          your use of babblebots.ai and its applications (together or
          individually “Service”) operated by Mystina AI Labs Private Limited
          and its subsidiaries.
        </Typography>
        <Typography>
          Our{" "}
          <a
            href="https://babblebots.ai/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "grey" }}
          >
            Privacy Policy
          </a>{" "}
          also governs your use of our Service and explains how we collect,
          safeguard, and disclose information that results from your use of our
          web pages.
        </Typography>
        <Typography>
          Your agreement with us includes these Terms and our{" "}
          <a
            href="https://babblebots.ai/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "grey" }}
          >
            Privacy Policy
          </a>
          (“Agreements”). You acknowledge that you have read and understood the
          Agreements, and agree to be bound by them.
        </Typography>
        <Typography>
          If you do not agree with (or cannot comply with) Agreements, then you
          may not use the Service, but please let us know by emailing
          helpme@babblebots.ai so we can try to find a solution. These Terms
          apply to all visitors, users, and others who wish to access or use the
          Service.
        </Typography>

        <Typography variant="h4">2. Communications</Typography>
        <Typography>
          By using our Service, you agree to subscribe to newsletters, marketing
          or promotional materials, and other information we may send. However,
          you may opt out of receiving any, or all, of these communications from
          us by following the unsubscribe link or by emailing at
          helpme@babblebots.ai.
        </Typography>

        <Typography variant="h4">3. Paid Accounts</Typography>
        <Typography>
          <strong>Billing.</strong> If you purchase any “Subscription(s)”, you
          will provide complete and accurate billing information, including a
          valid payment method. For paid subscriptions that renew automatically,
          we will charge your payment method on each agreed-upon periodic
          renewal until you cancel. For one-time purchase credits, there will be
          no automatic payments. You’re responsible for all applicable taxes,
          and we’ll charge tax when required. If your payment cannot be
          completed, we may downgrade your account or suspend your access to our
          Services until payment is received. You may cancel your Subscription
          renewal either through your online account management page or by
          contacting at helpme@babblebots.ai / customer support team.
        </Typography>
        <Typography>
          Should you run out of credits and/or the billing fails for any reason,
          Babblebots reserves the right to terminate your access to the Service
          with immediate effect.
        </Typography>
        <Typography variant="h4">4. Cancellation & Termination</Typography>
        <Typography>
          <strong>Cancellation.</strong> You can cancel your paid subscription
          at any time. Payments are non-refundable, except where required by
          law. These Terms do not override any mandatory local laws regarding
          your cancellation rights. We may employ the use of third-party
          services to facilitate payment and the completion of Purchases. By
          submitting your information, you grant us the right to provide the
          information to these third parties subject to our Privacy Policy.
        </Typography>
        <Typography>
          <strong>Termination.</strong> You are free to stop using our Services
          at any time. Either party may also terminate the Agreement upon thirty
          (30) days’ notice (or without notice in the case of nonpayment) if the
          other party materially breaches any of the terms or conditions of this
          Agreement. The customer will pay in full for the Services up to and
          including the last day on which the Services are provided.
        </Typography>
        <Typography>
          Upon any termination, the Company will make all Customer Data
          available to Customer for electronic retrieval for thirty (30) days.
          All sections of this Agreement which by their nature should survive
          termination will survive termination, including, without limitation,
          accrued rights to payment, confidentiality obligations, warranty
          disclaimers, and limitations of liability.
        </Typography>
        <Typography>
          We reserve the right to suspend or terminate your access to our
          Services or delete your account if we determine: - You breached these
          Terms or our Usage Policies.
          <br /> - We must do so to comply with the law. - Your use of our
          Services could cause risk or harm to Babblebots.ai, our users, or
          anyone else. - We also may terminate your account if it has been
          inactive for 90 days or more and you do not have a paid account.
        </Typography>
        <Typography>
          <strong>Appeals.</strong> If you believe we have suspended or
          terminated your account in error, you can file an appeal with us by
          contacting our Support team.
        </Typography>

        <Typography variant="h4">5. Discontinuation of Services</Typography>
        <Typography>
          We may decide to discontinue our Services, but if we do, we will give
          you advance notice and a refund for any prepaid, unused Services.
        </Typography>
        <Typography variant="h4">6. Refund Policy</Typography>
        <Typography>
          There shall be no refunds for cancellation of the credits and
          Subscription.
        </Typography>

        <Typography variant="h4">7. Shipping Policy</Typography>
        <Typography>
          As our products are digital, there is no physical shipping involved.
          All product deliveries are conducted electronically via email or
          through our online platform.
        </Typography>

        <Typography variant="h4">8. Fee Changes</Typography>
        <Typography>
          Babblebots, in its sole discretion and at any time, may modify Credit
          fees for the Subscriptions. Each credit comes with a set time window
          within which it should be consumed. Any fee change will become
          effective at the end of the contract window. Babblebots will provide
          you with a reasonable prior notice of any change in fees. Your
          continued use of Service after Subscription fee change comes into
          effect constitutes your agreement to pay the modified Subscription fee
          amount.
        </Typography>

        <Typography variant="h4">
          9. Confidentiality; Proprietary Rights
        </Typography>
        <Typography>
          We agree: (i) to take reasonable precautions to protect Proprietary
          Information (includes non-public information regarding features,
          functionality and performance of the Service), and (ii) not to use
          (except in performance of the Services or as otherwise permitted
          herein) or divulge to any third person any such Proprietary
          Information.
        </Typography>
        <Typography>
          You agree that the foregoing shall not apply with respect to any
          information after five (5) years following the disclosure thereof or
          any information that we can document (a) is or becomes generally
          available to the public, or (b) was in its possession or known by it
          prior to receipt from you, or (c) was rightfully disclosed to it
          without restriction by a third party, or (d) was independently
          developed without use of any of your Proprietary Information or (e) is
          required to be disclosed by law.
        </Typography>
        <Typography>
          Customer shall own all right, title and interest in and to the
          Customer Data as provided to Company. Any derived data via user
          engagement that is based on or derived from the Customer Data and
          provided to Customer as part of the Services shall be co-owned by both
          Parties. Company shall own and retain all right, title and interest in
          and to
        </Typography>
        <Typography>
          (a) the Services and Software, all improvements, enhancements or
          modifications thereto,
        </Typography>
        <Typography>
          (b) any software, applications, inventions or other technology
          developed in connection with Implementation Services or support,
          and{" "}
        </Typography>
        <Typography>
          (c) all intellectual property rights related to any of the foregoing.
        </Typography>
        <Typography>
          [Notwithstanding anything to the contrary, Company shall have the
          right collect and analyze data and other information relating to the
          provision, use and performance of various aspects of the Services and
          related systems and technologies (including, without limitation,
          information concerning Customer Data and data derived therefrom), and
          Company will be free (during and after the term hereof) to{" "}
        </Typography>
        <Typography>
          (i) use such information and data to improve and enhance the Services
          and for other development, diagnostic and corrective purposes in
          connection with the Services and other Company offerings, and
        </Typography>
        <Typography>
          (ii) disclose such data solely in aggregate or other de-identified
          form in connection with its business. No rights or licenses are
          granted except as expressly set forth herein.
        </Typography>
        <Typography variant="h4">
          10. Restrictions & Responsibilities
        </Typography>
        <Typography>
          When you use our Services you understand and agree:
        </Typography>
        <Typography>
          Customer will not, directly or indirectly: reverse engineer,
          decompile, disassemble or otherwise attempt to discover the source
          code, object code or underlying structure, ideas, know-how or
          algorithms relevant to the Services or any software, documentation or
          data related to the Services (“Software”); modify, translate, or
          create derivative works based on the Services or any Software (except
          to the extent expressly permitted by Company or authorized within the
          Services); use the Services or any Software for timesharing or service
          bureau purposes or otherwise for the benefit of a third; or remove any
          proprietary notices or labels.
        </Typography>
        <Typography>
          Customer represents, covenants, and warrants that Customer will use
          the Services only in compliance with Company’s standard published
          policies then in effect (the “Policy”) and all applicable laws and
          regulations. Customer hereby agrees to indemnify and hold harmless
          Company against any damages, losses, liabilities, settlements and
          expenses (including without limitation costs and attorneys’ fees) in
          connection with any claim or action that arises from an alleged
          violation of the foregoing or otherwise from Customer’s use of
          Services.
        </Typography>
        <Typography>
          Customer shall be responsible for obtaining and maintaining any
          equipment and ancillary services needed to connect to, access or
          otherwise use the Services, including, without limitation, modems,
          hardware, servers, software, operating systems, networking, web
          servers and the like (collectively, “Equipment”). Customer shall also
          be responsible for maintaining the security of the Equipment, Customer
          account, passwords (including but not limited to administrative and
          user passwords) and files, and for all uses of Customer account or the
          Equipment with or without Customer’s knowledge or consent.
        </Typography>

        <Typography variant="h4">11. Prohibited Uses</Typography>
        <Typography>
          You may use Service only for lawful purposes and in accordance with
          Terms. You agree not to use Service:
        </Typography>
        <Typography>
          0.1. In any way that violates any applicable national or international
          law or regulation.
        </Typography>
        <Typography>
          0.2. For the purpose of exploiting, harming, or attempting to exploit
          or harm minors in any way by exposing them to inappropriate content or
          otherwise.
        </Typography>
        <Typography>
          0.3. To transmit, or procure the sending of, any advertising or
          promotional material, including any “junk mail”, “chain letter,”
          “spam,” or any other similar solicitation.
        </Typography>
        <Typography>
          0.4. To impersonate or attempt to impersonate Company, a Company
          employee, another user, or any other person or entity.
        </Typography>
        <Typography>
          0.5. In any way that infringes upon the rights of others, or in any
          way is illegal, threatening, fraudulent, or harmful, or in connection
          with any unlawful, illegal, fraudulent, or harmful purpose or
          activity.
        </Typography>
        <Typography>
          0.6. To engage in any other conduct that restricts or inhibits
          anyone’s use or enjoyment of Service, or which, as determined by us,
          may harm or offend Company or users of Service or expose them to
          liability.
        </Typography>

        <Typography>Additionally, you agree not to:</Typography>
        <Typography>
          0.1. Use Service in any manner that could disable, overburden, damage,
          or impair Service or interfere with any other party’s use of Service,
          including their ability to engage in real-time activities through
          Service.
        </Typography>
        <Typography>
          0.2. Use any robot, spider, or other automatic device, process, or
          means to access Service for any purpose, including monitoring or
          copying any of the material on Service.
        </Typography>
        <Typography>
          0.3. Use any manual process to monitor or copy any of the material on
          Service or for any other unauthorized purpose without our prior
          written consent.
        </Typography>
        <Typography>
          0.4. Use any device, software, or routine that interferes with the
          proper working of Service.
        </Typography>
        <Typography>
          0.5. Introduce any viruses, trojan horses, worms, logic bombs, or
          other material which is malicious or technologically harmful.
        </Typography>
        <Typography>
          0.6. Attempt to gain unauthorized access to, interfere with, damage,
          or disrupt any parts of Service, the server on which Service is
          stored, or any server, computer, or database connected to Service.
        </Typography>
        <Typography>
          0.7. Attack Service via a denial-of-service attack or a distributed
          denial-of-service attack.
        </Typography>
        <Typography>
          0.8. Take any action that may damage or falsify Company rating.
        </Typography>
        <Typography>
          0.9. Otherwise attempt to interfere with the proper working of
          Service.
        </Typography>
        <Typography variant="h4">12. Analytics</Typography>
        <Typography>
          We may use third-party Service Providers to monitor and analyze the
          use of our Service.
        </Typography>

        <Typography variant="h4">13. No Use By Minors</Typography>
        <Typography>
          Service is intended only for access and use by individuals at least
          eighteen (18) years old. By accessing or using Service, you warrant
          and represent that you are at least eighteen (18) years of age and
          with the full authority, right, and capacity to enter into this
          agreement and abide by all of the terms and conditions of Terms. If
          you are not at least eighteen (18) years old, you are prohibited from
          both the access and usage of Service.
        </Typography>

        <Typography variant="h4">14. Accounts</Typography>
        <Typography>
          When you create an account with us, you guarantee that you are above
          the age of 18, and that the information you provide us is accurate,
          complete, and current at all times. Inaccurate, incomplete, or
          obsolete information may result in the immediate termination of your
          account on Service.
        </Typography>
        <Typography>
          You are responsible for maintaining the confidentiality of your
          account and password, including but not limited to the restriction of
          access to your computer and/or account. You agree to accept
          responsibility for any and all activities or actions that occur under
          your account and/or password, whether your password is with our
          Service or a third-party service. You must notify us immediately upon
          becoming aware of any breach of security or unauthorized use of your
          account.
        </Typography>
        <Typography>
          You may not use as a username the name of another person or entity or
          that is not lawfully available for use, a name or trademark that is
          subject to any rights of another person or entity other than you,
          without appropriate authorization. You may not use as a username any
          name that is offensive, vulgar or obscene.
        </Typography>
        <Typography>
          We reserve the right to refuse service, terminate accounts, remove or
          edit content, or cancel orders at our sole discretion.
        </Typography>
        <Typography variant="h4">15. Copyright Policy</Typography>
        <Typography>
          We respect the intellectual property rights of others. It is our
          policy to respond to any claim that Content posted on Service
          infringes on the copyright or other intellectual property rights
          (“Infringement”) of any person or entity.
        </Typography>
        <Typography>
          If you are a copyright owner, or authorized on behalf of one, and you
          believe that the copyrighted work has been copied in a way that
          constitutes copyright infringement, please submit your claim via email
          to helpme@babblebots.ai, with the subject line: “Copyright
          Infringement” and include in your claim a detailed description of the
          alleged Infringement.
        </Typography>
        <Typography>
          Written claims concerning copyright infringement must include the
          following information:
        </Typography>
        <ul style={{ paddingLeft: "5%" }}>
          <li>
            <Typography>
              A physical or electronic signature of the person authorized to act
              on behalf of the owner of the copyright interest.
            </Typography>
          </li>
          <li>
            <Typography>
              A description of the copyrighted work that you claim has been
              infringed upon.{" "}
            </Typography>
          </li>
          <li>
            <Typography>
              A description of where the allegedly infringing material is
              located on our site so we can find it.{" "}
            </Typography>
          </li>
          <li>
            <Typography>
              Your address, telephone number, and e-mail address.{" "}
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              A statement by you that you have a good-faith belief that the
              disputed use is not authorized by the copyright owner, its agent,
              or the law.{" "}
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              A statement by you that the above information in your notice is
              accurate and, under penalty of perjury, that you are the copyright
              owner or authorized to act on the copyright owner’s behalf.{" "}
            </Typography>
          </li>
        </ul>
        <Typography>
          You may be held accountable for damages (including costs and
          attorneys’ fees) for misrepresentation or bad-faith claims on the
          infringement of any Content found on and/or through Service on your
          copyright.
        </Typography>

        <Typography variant="h4">16. Error Reporting and Feedback</Typography>
        <Typography>
          You may provide us either directly at helpme@babblebots.ai or via
          third-party sites and tools with information and feedback concerning
          errors, suggestions for improvements, ideas, problems, complaints, and
          other matters related to our Service (“Feedback”).
        </Typography>
        <Typography>
          You acknowledge and agree that: (i) you shall not retain, acquire or
          assert any intellectual property right or other right, title or
          interest in or to the Feedback; (ii) Company may have development
          ideas similar to the Feedback; (iii) Feedback does not contain
          confidential information or proprietary information from you or any
          third party; and (iv) Company is not under any obligation of
          confidentiality with respect to the Feedback.
        </Typography>
        <Typography>
          In the event the transfer of the ownership to the Feedback is not
          possible due to applicable mandatory laws, you grant Company and its
          affiliates an exclusive, transferable, irrevocable, free-of-charge,
          sub-licensable, unlimited and perpetual right to use (including copy,
          modify, create derivative works, publish, distribute and
          commercialize) Feedback in any manner and for any purpose.
        </Typography>

        <Typography variant="h4">17. Links To Other Websites</Typography>
        <Typography>
          Our Service may contain links to third-party websites or services that
          are not owned or controlled by Babblebots.
        </Typography>
        <Typography>
          Babblebots has no control over, and assumes no responsibility for the
          content, privacy policies, or practices of any third-party websites or
          services. We do not warrant the offerings of any of these
          entities/individuals or their websites.
        </Typography>
        <Typography fontWeight="bold">
          YOU ACKNOWLEDGE AND AGREE THAT COMPANY SHALL NOT BE RESPONSIBLE OR
          LIABLE, DIRECTLY OR INDIRECTLY, FOR ANY DAMAGE OR LOSS CAUSED OR
          ALLEGED TO BE CAUSED BY OR IN CONNECTION WITH USE OF OR RELIANCE ON
          ANY SUCH CONTENT, GOODS OR SERVICES AVAILABLE ON OR THROUGH ANY SUCH
          THIRD-PARTY WEBSITES OR SERVICES.
        </Typography>
        <Typography>
          WE STRONGLY ADVISE YOU TO READ THE TERMS OF SERVICE AND PRIVACY
          POLICIES OF ANY THIRD-PARTY WEBSITES OR SERVICES THAT YOU VISIT.
        </Typography>

        <Typography variant="h4">18. Disclaimer Of Warranty</Typography>
        <Typography fontWeight="bold">
          OUR SERVICES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. WE
          MAKE NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
          AS TO THE OPERATION OF THEIR SERVICES, OR THE INFORMATION, CONTENT OR
          MATERIALS INCLUDED THEREIN.
        </Typography>
        <Typography>
          YOU EXPRESSLY AGREE THAT YOUR USE OF THESE SERVICES, THEIR CONTENT,
          AND ANY SERVICES OR ITEMS OBTAINED FROM US IS AT YOUR SOLE RISK.
          COMPANY DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR
          ERROR-FREE; NOR DOES IT MAKE ANY WARRANTY AS TO THE RESULTS THAT MAY
          BE OBTAINED FROM USE OF THE SERVICES.
        </Typography>
        <Typography>
          EXCEPT AS EXPRESSLY SET FORTH IN THIS SECTION, THE SERVICES AND
          IMPLEMENTATION SERVICES ARE PROVIDED “AS IS” AND COMPANY DISCLAIMS ALL
          WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED
          WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE AND
          NON-INFRINGEMENT.
        </Typography>

        <Typography variant="h4">19. Limitation Of Liability</Typography>
        <Typography fontWeight="bold">
          EXCEPT AS PROHIBITED BY LAW, YOU WILL HOLD US AND OUR OFFICERS,
          DIRECTORS, EMPLOYEES, AND AGENTS HARMLESS FOR ANY INDIRECT, PUNITIVE,
          SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGE, HOWEVER IT ARISES
          (INCLUDING ATTORNEYS’ FEES AND ALL RELATED COSTS AND EXPENSES OF
          LITIGATION AND ARBITRATION, OR AT TRIAL OR ON APPEAL, IF ANY, WHETHER
          OR NOT LITIGATION OR ARBITRATION IS INSTITUTED), WHETHER IN AN ACTION
          OF CONTRACT, NEGLIGENCE, OR OTHER TORTIOUS ACTION, OR ARISING OUT OF
          OR IN CONNECTION WITH THIS AGREEMENT.
        </Typography>
        <Typography>
          INCLUDING WITHOUT LIMITATION ANY CLAIM FOR PERSONAL INJURY OR PROPERTY
          DAMAGE, ARISING FROM THIS AGREEMENT AND ANY VIOLATION BY YOU OF ANY
          FEDERAL, STATE, OR LOCAL LAWS, STATUTES, RULES, OR REGULATIONS, EVEN
          IF COMPANY HAS BEEN PREVIOUSLY ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGE.
        </Typography>
        <Typography>
          EXCEPT AS PROHIBITED BY LAW, IF THERE IS LIABILITY FOUND ON THE PART
          OF COMPANY, IT WILL BE LIMITED TO THE AMOUNT PAID FOR THE PRODUCTS
          AND/OR SERVICES, AND UNDER NO CIRCUMSTANCES WILL THERE BE
          CONSEQUENTIAL OR PUNITIVE DAMAGES.
        </Typography>
        <Typography>
          SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF PUNITIVE,
          INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE PRIOR LIMITATION OR
          EXCLUSION MAY NOT APPLY TO YOU.
        </Typography>
        <Typography variant="h4">20. Indemnity</Typography>
        <Typography>
          Company shall hold Customer harmless from liability to third parties
          resulting from infringement by the Service of any patent or any
          copyright or misappropriation of any trade secret, provided Company is
          promptly notified of any and all threats, claims, and proceedings
          related thereto and given reasonable assistance and the opportunity to
          assume sole control over defense and settlement; Company will not be
          responsible for any settlement it does not approve in writing.
        </Typography>
        <Typography>
          The foregoing obligations do not apply with respect to portions or
          components of the Service:
        </Typography>

        <ul style={{ paddingLeft: "5%" }}>
          <li>
            {" "}
            <Typography> Not supplied by Company. </Typography>
          </li>
          <li>
            <Typography>
              Made in whole or in part in accordance with Customer
              specifications.
            </Typography>
          </li>
          <li>
            <Typography>
              That are modified after delivery by the Company.
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              Combined with other products, processes, or materials where the
              alleged infringement relates to such combination.
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              Where Customer continues allegedly infringing activity after being
              notified thereof or after being informed of modifications that
              would have avoided the alleged infringement.{" "}
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              Where Customer’s use of the Service is not strictly in accordance
              with this Agreement.{" "}
            </Typography>
          </li>
        </ul>

        <Typography>
          If, due to a claim of infringement, the Services are held by a court
          of competent jurisdiction to be or are believed by the Company to be
          infringing, the Company may, at its option and expense:
        </Typography>
        <ul style={{ paddingLeft: "5%" }}>
          <li>
            {" "}
            <Typography>
              Replace or modify the Service to be non-infringing provided that
              such modification or replacement contains substantially similar
              features and functionality.{" "}
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              Obtain for Customer a license to continue using the Service.
            </Typography>
          </li>
          <li>
            {" "}
            <Typography>
              If neither of the foregoing is commercially practicable, terminate
              this Agreement and Customer’s rights hereunder and provide
              Customer a refund of any prepaid, unused fees for the
              Service.{" "}
            </Typography>
          </li>
        </ul>

        <Typography variant="h4">21. Governing Law</Typography>
        <Typography>
          These Terms of Use are governed by the laws of India, without regard
          to conflict-of-law principles. You agree that any action, dispute, or
          claim arising out of or in connection with these Terms of Use shall be
          subject to the exclusive jurisdiction of the courts in Mumbai,
          Maharashtra.
        </Typography>
        <Typography>
          The laws of India shall govern this Agreement and your use of the
          website. Your use of the website may also be subject to applicable
          local, state, national, or international laws.
        </Typography>
        <Typography>
          By using Babblebots or contacting us directly, you indicate your
          acceptance of these Terms of Use. If you do not agree to these Terms
          of Use, you should refrain from using our website or services.
          Continued use of the website, continued engagement with us, or
          continued usage following any updates to these Terms of Use—so long as
          such updates do not materially affect the use or disclosure of your
          personal information—will constitute your acceptance of the revised
          terms.
        </Typography>

        <Typography variant="h4">22. Changes To Service</Typography>
        <Typography>
          We reserve the right to withdraw or amend our Service, and any service
          or material we provide via Service, in our sole discretion without
          notice. We will not be liable if for any reason all or any part of the
          Service is unavailable at any time or for any period. From time to
          time, we may restrict access to some parts of the Service, or the
          entire Service, to users, including registered users.
        </Typography>

        <Typography variant="h4">23. Amendments To Terms</Typography>
        <Typography>
          We may amend the Terms at any time by posting the amended terms on
          this site. It is your responsibility to review these Terms
          periodically.
        </Typography>
        <Typography>
          Your continued use of the Platform following the posting of revised
          Terms means that you accept and agree to the changes. You are expected
          to check this page frequently so you are aware of any changes, as they
          are binding on you.
        </Typography>
        <Typography>
          By continuing to access or use our Service after any revisions become
          effective, you agree to be bound by the revised terms. If you do not
          agree to the new terms, you are no longer authorized to use the
          Service.
        </Typography>

        <Typography variant="h4">24. Waiver And Severability</Typography>
        <Typography>
          No waiver by the Company of any term or condition set forth in Terms
          shall be deemed a further or continuing waiver of such term or
          condition or a waiver of any other term or condition, and any failure
          of the Company to assert a right or provision under Terms shall not
          constitute a waiver of such right or provision.
        </Typography>
        <Typography>
          If any provision of Terms is held by a court or other tribunal of
          competent jurisdiction to be invalid, illegal, or unenforceable for
          any reason, such provision shall be eliminated or limited to the
          minimum extent such that the remaining provisions of Terms will
          continue in full force and effect.
        </Typography>

        <Typography variant="h4">25. Marketing</Typography>
        <Typography>
          Babblebots may use Customer’s name and logo on their website and other
          marketing materials solely to identify Customer as a Customer of
          Babblebots (without revealing any Confidential Information).
        </Typography>

        <Typography variant="h4">26. Acknowledgement</Typography>
        <Typography fontWeight="bold">
          BY USING SERVICE OR OTHER SERVICES PROVIDED BY US, YOU ACKNOWLEDGE
          THAT YOU HAVE READ THESE TERMS OF SERVICE AND AGREE TO BE BOUND BY
          THEM.
        </Typography>

        <Typography variant="h4">27. Contact Us</Typography>
        <Typography>
          Please send your feedback, comments, and requests for technical
          support by email: helpme@babblebots.ai.
        </Typography>
      </Stack>
    </Stack>
  );
}

export default TermsAndConditions;
